/**
 * Admin › Users — DESIGN.md §9.3 (R21).
 *
 * "add (name, email, role, groups → invite email), deactivate (blocks login,
 * keeps attribution everywhere), reactivate, role change. All audited."
 *
 * This is M0's acceptance surface: Brad adds a user here, the invite lands in
 * Mailpit, and the new user signs in.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Mail, Search, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { USER_ROLES, type User, type UserRole } from '@slate/shared';
import { ApiError } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';
import {
  useCreateUser,
  useGroups,
  useReinvite,
  useSetUserActive,
  useUpdateUser,
  useUsers,
} from '@/features/admin/users-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatar } from '@/components/ui/avatar';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion, Spinner } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Combobox } from '@/components/ui/combobox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersPage,
});

function AdminUsersPage(): JSX.Element {
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const users = useUsers({ active: showInactive ? 'all' : 'true', q: query });

  /**
   * The row mutations are owned here, not inside `UserRowItem`.
   *
   * Deactivating a user removes their row from the default (active-only)
   * list, so the row unmounts the moment the mutation succeeds — and React
   * Query does not run `mutate()`-level callbacks for an unmounted observer.
   * Owning them on the page, which never unmounts, is what makes the
   * confirmation toast reliable.
   */
  const updateUser = useUpdateUser();
  const setActive = useSetUserActive();
  const reinvite = useReinvite();

  const actions: RowActions = {
    changeRole: (user, role) =>
      updateUser.mutate(
        { id: user.id, role },
        {
          onSuccess: () =>
            toast.success(
              `${user.name} is now ${role === 'admin' ? 'an administrator' : 'a member'}`,
            ),
          onError: (error) =>
            toast.error(error instanceof ApiError ? error.message : 'Could not change role'),
        },
      ),
    setActive: (user, isActive) =>
      setActive.mutate(
        { id: user.id, isActive },
        {
          onSuccess: (updated) =>
            toast.success(
              updated.isActive
                ? `${updated.name} can sign in again`
                : `${updated.name} has been deactivated — their history is kept`,
            ),
          onError: (error) =>
            toast.error(error instanceof ApiError ? error.message : 'Could not update'),
        },
      ),
    reinvite: (user) =>
      reinvite.mutate(user.id, {
        onSuccess: () => toast.success(`Invite re-sent to ${user.email}`),
        onError: (error) =>
          toast.error(error instanceof ApiError ? error.message : 'Could not re-invite'),
      }),
    isBusy: updateUser.isPending || setActive.isPending || reinvite.isPending,
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search
            className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2"
            aria-hidden="true"
          />
          <Input
            className="pl-8"
            placeholder="Search by name or email"
            aria-label="Search users"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <label className="text-muted-foreground flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={showInactive}
            onCheckedChange={(checked) => setShowInactive(checked === true)}
          />
          Show deactivated
        </label>
        <Button onClick={() => setAddOpen(true)}>
          <UserPlus />
          Add user
        </Button>
      </div>

      {users.isLoading ? (
        <LoadingRegion label="Loading users" />
      ) : users.data && users.data.items.length > 0 ? (
        <div className="bg-card rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden sm:table-cell">Groups</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">Access</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.data.items.map((user) => (
                <UserRowItem key={user.id} user={user} actions={actions} />
              ))}
            </TableBody>
          </Table>
          <p className="text-muted-foreground border-t px-3 py-2 text-xs">
            {users.data.totalCount} {users.data.totalCount === 1 ? 'person' : 'people'}
          </p>
        </div>
      ) : (
        <EmptyState
          icon={<Users className="size-8" />}
          title={query ? 'No one matches that search' : 'No users yet'}
          description={
            query
              ? 'Try a different name or email address, or clear the search.'
              : 'Add your teammates here. Each one gets an invite email to set their own password — nobody can sign themselves up.'
          }
          action={
            query ? undefined : (
              <Button onClick={() => setAddOpen(true)}>
                <UserPlus />
                Add the first user
              </Button>
            )
          }
        />
      )}

      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

interface RowActions {
  changeRole: (user: User, role: UserRole) => void;
  setActive: (user: User, isActive: boolean) => void;
  reinvite: (user: User) => void;
  isBusy: boolean;
}

function UserRowItem({ user, actions }: { user: User; actions: RowActions }): JSX.Element {
  const me = useCurrentUser();
  const isSelf = user.id === me.id;

  return (
    <TableRow className={user.isActive ? undefined : 'opacity-60'}>
      <TableCell>
        <div className="flex items-center gap-2.5">
          <UserAvatar user={user} />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {user.name}
              {isSelf ? <span className="text-muted-foreground ml-1.5 text-xs">(you)</span> : null}
            </p>
            <p className="text-muted-foreground truncate text-xs">{user.email}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <div className="flex flex-wrap gap-1">
          {user.groups.length > 0 ? (
            user.groups.map((group) => (
              <Badge key={group.id} variant="secondary">
                {group.name}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Combobox
          className="w-[9.5rem]"
          label={`Role for ${user.name}`}
          value={user.role}
          disabled={actions.isBusy}
          options={USER_ROLES.map((role) => ({
            value: role,
            label: role === 'admin' ? 'Administrator' : 'Member',
          }))}
          onChange={(role) => actions.changeRole(user, role as UserRole)}
        />
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="flex flex-wrap gap-1">
          {!user.isActive ? <Badge variant="destructive">Deactivated</Badge> : null}
          {user.hasGoogle ? <Badge variant="secondary">Google</Badge> : null}
          {user.hasPassword ? (
            <Badge variant="secondary">Password</Badge>
          ) : (
            <Badge variant="warning">Invite pending</Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {user.isActive && !user.hasPassword ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={actions.isBusy}
              onClick={() => actions.reinvite(user)}
            >
              <Mail />
              Re-invite
            </Button>
          ) : null}
          <Button
            variant={user.isActive ? 'ghost' : 'outline'}
            size="sm"
            // §9.3 leaves no way to delete a user, and deactivating yourself
            // would lock you out of the admin area you are standing in.
            disabled={isSelf || actions.isBusy}
            title={isSelf ? 'You cannot deactivate your own account' : undefined}
            onClick={() => actions.setActive(user, !user.isActive)}
          >
            {user.isActive ? 'Deactivate' : 'Reactivate'}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function AddUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const createUser = useCreateUser();
  const groups = useGroups();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [groupIds, setGroupIds] = useState<string[]>([]);

  const reset = (): void => {
    setEmail('');
    setName('');
    setRole('member');
    setGroupIds([]);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a user</DialogTitle>
          <DialogDescription>
            They receive an invite email and choose their own password. The link expires in seven
            days.
          </DialogDescription>
        </DialogHeader>

        <form
          id="add-user"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            createUser.mutate(
              { email, name, role, groupIds, sendInvite: true },
              {
                onSuccess: (user) => {
                  toast.success(`Invite sent to ${user.email}`);
                  reset();
                  onOpenChange(false);
                },
                onError: (error) =>
                  toast.error(error instanceof ApiError ? error.message : 'Could not add the user'),
              },
            );
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="new-name">Name</Label>
            <Input
              id="new-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-email">Email</Label>
            <Input
              id="new-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-role">Role</Label>
            <Combobox
              id="new-role"
              label="Role"
              className="h-9"
              value={role}
              options={[
                { value: 'member', label: 'Member' },
                { value: 'admin', label: 'Administrator' },
              ]}
              onChange={(value) => setRole(value as UserRole)}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Groups</legend>
            {groups.data?.length ? (
              <div className="flex flex-wrap gap-3">
                {groups.data.map((group) => (
                  <label key={group.id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={groupIds.includes(group.id)}
                      onCheckedChange={(checked) =>
                        setGroupIds((current) =>
                          checked === true
                            ? [...current, group.id]
                            : current.filter((id) => id !== group.id),
                        )
                      }
                    />
                    {group.name}
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No groups yet.</p>
            )}
          </fieldset>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-user" disabled={createUser.isPending}>
            {createUser.isPending ? <Spinner /> : null}
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
